import type { ThemeKey } from "@/shared/hooks/useTheme";

/** 机器人造型 = 皮肤 key。三套皮肤不只换色,骨架/性格也各自成立。 */
export type RobotVariant = ThemeKey;
export const ROBOT_VARIANTS: RobotVariant[] = ["cyber", "comic", "standard"];

/**
 * ★data-part 契约(useRobotMotion 靠它选元素驱动动效):**每套造型都必须提供**
 * `eye-l` / `eye-r` / `glow`,否则 gsap 收到空目标 → 控制台刷 "GSAP target not found"、
 * 该部件动效静默消失。单测 AdvisorRobot.test.tsx 锚定。
 *
 * <p>着色只用 design token:机身 currentColor(继承按钮的 text-primary-foreground)、
 * 亮色件 hsl(var(--primary))。硬编码颜色会让某套皮肤下瞎掉(实测:三皮肤 primary 分别是
 * 青 / 粉 / 靛蓝,primary-foreground 分别是近黑 / 白 / 白)。
 */
interface RobotProps {
  variant?: RobotVariant;
  className?: string;
}

const svgProps = {
  viewBox: "0 0 48 48",
  // 天线要探出头顶,不能被裁
  style: { overflow: "visible" as const },
  "aria-hidden": true,
  focusable: "false" as const,
};

/** 霓虹科技:HUD 感 —— 细天线、方腮、发光面罩。 */
function CyberRobot({ className }: { className?: string }) {
  return (
    <svg {...svgProps} className={className}>
      <g data-part="antenna" style={{ transformOrigin: "24px 15px" }}>
        <line
          x1="24"
          y1="15"
          x2="24"
          y2="7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle data-part="glow" cx="24" cy="5" r="2.8" fill="hsl(var(--primary))" />
        <circle cx="24" cy="5" r="2.8" fill="none" stroke="currentColor" strokeWidth="1.2" />
      </g>
      {/* 方腮(耳) */}
      <rect x="4" y="23" width="3.5" height="8" rx="1.75" fill="currentColor" />
      <rect x="40.5" y="23" width="3.5" height="8" rx="1.75" fill="currentColor" />
      <rect x="8" y="14" width="32" height="26" rx="9" fill="currentColor" />
      {/* 面罩:露出球底色 → 像亮着的屏幕 */}
      <rect x="12.5" y="19" width="23" height="16" rx="7" fill="hsl(var(--primary))" />
      <g data-part="eye-l" style={{ transformOrigin: "19px 27px" }}>
        <circle cx="19" cy="27" r="3" fill="currentColor" />
      </g>
      <g data-part="eye-r" style={{ transformOrigin: "29px 27px" }}>
        <circle cx="29" cy="27" r="3" fill="currentColor" />
      </g>
    </svg>
  );
}

/** 漫画可爱:粗描边、圆胖、腮红、笑眼(弧线嘴角朝上)。 */
function ComicRobot({ className }: { className?: string }) {
  return (
    <svg {...svgProps} className={className}>
      <g data-part="antenna" style={{ transformOrigin: "24px 14px" }}>
        <path
          d="M24 14 C24 10, 26 8, 27 6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
        />
        <circle data-part="glow" cx="27.6" cy="4.6" r="3.4" fill="currentColor" />
        <circle
          cx="27.6"
          cy="4.6"
          r="3.4"
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="1.6"
        />
      </g>
      {/* 圆胖机身 + 粗描边(描边用 primary,在同色球面上勾出轮廓) */}
      <rect
        x="6"
        y="13"
        width="36"
        height="29"
        rx="14"
        fill="currentColor"
        stroke="hsl(var(--primary))"
        strokeWidth="2.4"
      />
      {/* 圆耳 */}
      <circle
        cx="5"
        cy="28"
        r="3.2"
        fill="currentColor"
        stroke="hsl(var(--primary))"
        strokeWidth="1.8"
      />
      <circle
        cx="43"
        cy="28"
        r="3.2"
        fill="currentColor"
        stroke="hsl(var(--primary))"
        strokeWidth="1.8"
      />
      {/* 笑眼:弧线开口朝上 */}
      <g data-part="eye-l" style={{ transformOrigin: "18px 26px" }}>
        <path
          d="M14.4 27.6 Q18 22.6, 21.6 27.6"
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </g>
      <g data-part="eye-r" style={{ transformOrigin: "30px 26px" }}>
        <path
          d="M26.4 27.6 Q30 22.6, 33.6 27.6"
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </g>
      {/* 腮红 */}
      <ellipse cx="13" cy="33.5" rx="3.1" ry="2" fill="hsl(var(--primary))" opacity="0.55" />
      <ellipse cx="35" cy="33.5" rx="3.1" ry="2" fill="hsl(var(--primary))" opacity="0.55" />
      {/* 笑嘴 */}
      <path
        d="M20.5 34.5 Q24 37.8, 27.5 34.5"
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** 中规中矩:极简几何 —— 无天线、扁平、方圆头 + 一颗状态灯。 */
function StandardRobot({ className }: { className?: string }) {
  return (
    <svg {...svgProps} className={className}>
      {/* 无天线:glow 改由额前状态灯承担(契约仍需 glow,呼吸脉冲挂在它身上) */}
      <rect x="9" y="12" width="30" height="28" rx="7" fill="currentColor" />
      {/* 额前状态灯 */}
      <circle data-part="glow" cx="24" cy="17" r="1.9" fill="hsl(var(--primary))" />
      {/* 扁平面罩:直角圆润的一条横窗 */}
      <rect x="13.5" y="22" width="21" height="12" rx="4" fill="hsl(var(--primary))" />
      <g data-part="eye-l" style={{ transformOrigin: "19.5px 28px" }}>
        <rect x="17.6" y="25.6" width="3.8" height="4.8" rx="1.9" fill="currentColor" />
      </g>
      <g data-part="eye-r" style={{ transformOrigin: "28.5px 28px" }}>
        <rect x="26.6" y="25.6" width="3.8" height="4.8" rx="1.9" fill="currentColor" />
      </g>
      {/* 底座:极简一横 */}
      <rect x="19" y="41.2" width="10" height="2.4" rx="1.2" fill="currentColor" opacity="0.7" />
    </svg>
  );
}

const BY_VARIANT: Record<RobotVariant, (p: { className?: string }) => JSX.Element> = {
  cyber: CyberRobot,
  comic: ComicRobot,
  standard: StandardRobot,
};

/**
 * 军师小阳的机器人形象(纯呈现,零动画逻辑)。动效由 useRobotMotion 通过 data-part 选择器驱动
 * —— 不用 React state 驱动动画(每帧 setState 会把整棵树重渲染)。
 *
 * <p>造型跟随皮肤(cyber/comic/standard 各一套骨架);未知/缺省回落 cyber。
 */
export function AdvisorRobot({ variant = "cyber", className }: RobotProps) {
  const Robot = BY_VARIANT[variant] ?? CyberRobot;
  return <Robot className={className} />;
}
